import CONSTANTS from 'src/utils/constants';
import {
    CDTSegmentType,
    ColumnDataType,
    ColumnDef,
    ColumnSourceType,
    CreateSegmentDataTableRequest,
} from 'src/models/segment-cdt/SegmentCdtTypes';
import { CreateWizardState, SegmentColumnDraft } from 'src/services/segment-cdt/SegmentTablesState';

/**
 * Pure helpers for the create wizard — kept out of the components so payload building and validation
 * are unit-testable without mounting. All mirror the server-enforced rules (CR-299438935).
 */

/** A columnId derived from the display name: alphanumerics kept, runs of other chars → `_`, trimmed. */
export function toColumnId(displayName: string): string {
    return (displayName || '')
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

/** The table name is valid when it starts with the CDT prefix, has content after it, and uses only [A-Za-z0-9_]. */
export function isValidTableName(name: string): boolean {
    const prefix = CONSTANTS.CUSTOM_DATA_TABLE_PREFIX;
    if (!name || !name.startsWith(prefix) || name.length <= prefix.length) {
        return false;
    }
    return /^[A-Za-z0-9_]+$/.test(name);
}

/**
 * The COA segment name (`getCustomCOADefinitionsForSegment`'s argument) for a given table segment.
 * That API keys off the UI segment name — the same value MagicBox passes (e.g. 'Company', 'Cost Center').
 * `ENTITY` maps to COMPANY, since the COA mapping has no distinct ENTITY entry. Null until a segment is
 * chosen, so callers can leave the dropdown disabled with a "select a segment first" hint.
 */
export function segmentCoaName(segmentType?: CDTSegmentType): string | null {
    switch (segmentType) {
        case CDTSegmentType.COST_CENTER:
            return CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.UI;
        case CDTSegmentType.ENTITY:
            return CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI;
        case CDTSegmentType.LOCATION:
            return CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.UI;
        case CDTSegmentType.PRODUCT_LINE:
            return CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.UI;
        case CDTSegmentType.CHANNEL:
            return CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.UI;
        case CDTSegmentType.PROJECT:
            return CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.UI;
        default:
            return null;
    }
}

/** Human label for a column's source type (chooser + list). */
export function columnTypeLabel(sourceType?: ColumnSourceType): string {
    switch (sourceType) {
        case ColumnSourceType.PULL_GL:
            return 'GL Balance Pull';
        case ColumnSourceType.MANUAL:
            return 'Manual Upload';
        case ColumnSourceType.DERIVED_PCT:
            return 'Derived: % of total';
        case ColumnSourceType.DERIVED_BAL:
            return 'Derived balance';
        default:
            return String(sourceType ?? '');
    }
}

/**
 * Validate the in-progress column draft and build a `ColumnDef`, or return an error message.
 * `existingNames` is the display names of the OTHER committed columns (for the duplicate check).
 * `referenceColumns` is the columns a DERIVED column may point at (the table's existing columns plus
 * any already-queued additions); required for DERIVED_PCT / DERIVED_BAL, whose references must exist,
 * carry the expected dataType, and not themselves be derived (matches the backend
 * `SegmentColumnConfigValidator`, which keeps derivation a single hop). Emits only the fields the
 * contract stores, no stray keys.
 */
export function buildColumnDef(
    draft: SegmentColumnDraft,
    existingNames: string[],
    existingIds: string[] = [],
    validCurrencies: string[] = [],
    referenceColumns: ColumnDef[] = [],
): { column?: ColumnDef; error?: string } {
    const name = (draft.displayName || '').trim();
    if (!name) {
        return { error: 'Column name is required.' };
    }
    if (existingNames.some((n) => n.trim().toLowerCase() === name.toLowerCase())) {
        return { error: `A column named "${name}" already exists.` };
    }
    const columnId = toColumnId(name);
    if (!columnId) {
        return { error: 'Column name must contain a letter or number.' };
    }
    // Distinct display names can still collapse to the same columnId (e.g. "2D Revenue" and
    // "2D/Revenue" both become "2D_Revenue"), which the backend rejects for duplicate ids. Catch it here.
    if (existingIds.includes(columnId)) {
        return { error: `This name maps to the same column id ("${columnId}") as another column. Choose a more distinct name.` };
    }

    if (draft.sourceType === ColumnSourceType.MANUAL) {
        if (!draft.dataType) {
            return { error: 'Select a data type.' };
        }
        const column: ColumnDef = {
            columnId,
            displayName: name,
            sourceType: ColumnSourceType.MANUAL,
            dataType: draft.dataType,
        };
        if (draft.dataType === ColumnDataType.NUMBER) {
            if (!draft.currency) {
                return { error: 'Currency is required for a Number column.' };
            }
            if (validCurrencies.length && !validCurrencies.includes(draft.currency)) {
                return { error: `"${draft.currency}" is not a recognized currency code. Pick one from the list.` };
            }
            column.currency = draft.currency;
        }
        return { column };
    }

    if (draft.sourceType === ColumnSourceType.PULL_GL) {
        const pc = draft.pullCriteria || {};
        if (!pc.entity) {
            return { error: 'Company / Entity is required.' };
        }
        if (!pc.account) {
            return { error: 'GL Account is required.' };
        }
        if (!pc.periodType) {
            return { error: 'Period is required.' };
        }
        if (!draft.currency) {
            return { error: 'Currency is required.' };
        }
        if (validCurrencies.length && !validCurrencies.includes(draft.currency)) {
            return { error: `"${draft.currency}" is not a recognized currency code. Pick one from the list.` };
        }
        const column: ColumnDef = {
            columnId,
            displayName: name,
            sourceType: ColumnSourceType.PULL_GL,
            dataType: ColumnDataType.NUMBER,
            currency: draft.currency,
            pullCriteria: {
                entity: pc.entity,
                account: pc.account,
                periodType: pc.periodType,
                ...(pc.customCoaId ? { customCoaId: pc.customCoaId } : {}),
            },
        };
        return { column };
    }

    if (draft.sourceType === ColumnSourceType.DERIVED_PCT || draft.sourceType === ColumnSourceType.DERIVED_BAL) {
        const dc = draft.derivedConfig || {};
        const refIndex: Record<string, ColumnDef> = {};
        for (const c of referenceColumns) {
            if (c && c.columnId) {
                refIndex[c.columnId] = c;
            }
        }
        // A derived reference must exist, carry the expected dataType, and not itself be derived
        // (single-hop rule), mirroring SegmentColumnConfigValidator.requireReference/requireDataType.
        const checkRef = (fieldLabel: string, refId: string | undefined, wantType: ColumnDataType): string | undefined => {
            if (!refId) {
                return `${fieldLabel} is required.`;
            }
            if (refId === columnId) {
                return `${fieldLabel} cannot reference the column itself.`;
            }
            const referenced = refIndex[refId];
            if (!referenced) {
                return `${fieldLabel} references unknown column "${refId}".`;
            }
            if (referenced.dataType !== wantType) {
                return `${fieldLabel} must reference a ${wantType} column, but "${refId}" is ${referenced.dataType ?? 'undefined'}.`;
            }
            if (referenced.sourceType === ColumnSourceType.DERIVED_PCT || referenced.sourceType === ColumnSourceType.DERIVED_BAL) {
                return `${fieldLabel} must reference a non-derived column, but "${refId}" is ${referenced.sourceType}.`;
            }
            return undefined;
        };

        if (draft.sourceType === ColumnSourceType.DERIVED_PCT) {
            const pctErr = checkRef('% column', dc.pctColumnId, ColumnDataType.PERCENTAGE);
            if (pctErr) {
                return { error: pctErr };
            }
            const valueErr = checkRef('Value column', dc.valueColumnId, ColumnDataType.NUMBER);
            if (valueErr) {
                return { error: valueErr };
            }
            // DERIVED_PCT is always PERCENTAGE and carries no currency (the backend rejects one).
            const column: ColumnDef = {
                columnId,
                displayName: name,
                sourceType: ColumnSourceType.DERIVED_PCT,
                dataType: ColumnDataType.PERCENTAGE,
                derivedConfig: {
                    type: ColumnSourceType.DERIVED_PCT,
                    pctColumnId: dc.pctColumnId,
                    valueColumnId: dc.valueColumnId,
                },
            };
            return { column };
        }

        // DERIVED_BAL is a GL-pulling type: it carries its own pullCriteria AND a sourceColumnId.
        const pc = draft.pullCriteria || {};
        if (!pc.entity) {
            return { error: 'Company / Entity is required.' };
        }
        if (!pc.account) {
            return { error: 'GL Account is required.' };
        }
        if (!pc.periodType) {
            return { error: 'Period is required.' };
        }
        if (!draft.currency) {
            return { error: 'Currency is required.' };
        }
        if (validCurrencies.length && !validCurrencies.includes(draft.currency)) {
            return { error: `"${draft.currency}" is not a recognized currency code. Pick one from the list.` };
        }
        const sourceErr = checkRef('Source column', dc.sourceColumnId, ColumnDataType.NUMBER);
        if (sourceErr) {
            return { error: sourceErr };
        }
        const column: ColumnDef = {
            columnId,
            displayName: name,
            sourceType: ColumnSourceType.DERIVED_BAL,
            dataType: ColumnDataType.NUMBER,
            currency: draft.currency,
            pullCriteria: {
                entity: pc.entity,
                account: pc.account,
                periodType: pc.periodType,
                ...(pc.customCoaId ? { customCoaId: pc.customCoaId } : {}),
            },
            derivedConfig: {
                type: ColumnSourceType.DERIVED_BAL,
                sourceColumnId: dc.sourceColumnId,
            },
        };
        return { column };
    }

    return { error: 'Unsupported column type.' };
}

/**
 * Build the create request from the wizard state. `tableType` is not sent: CR-299438935 dropped it
 * from the request and the backend hardcodes SEGMENT_LEVEL.
 */
export function buildCreateRequest(cw: CreateWizardState): CreateSegmentDataTableRequest {
    return {
        tableName: cw.tableName.trim(),
        segmentType: cw.segmentType as CDTSegmentType,
        ...(cw.segmentCustomCoaId ? { segmentCustomCoaId: cw.segmentCustomCoaId } : {}),
        ...(cw.refreshCadence ? { refreshCadence: cw.refreshCadence } : {}),
        columns: cw.columns,
    };
}
